import { type BOL, getMappingDisplay } from '@/api/hooks/useOrderDetails'
import type { components } from '@/types/api/v1'
import { formatCompactDateTime } from '@/utils/dateFormatters'
import { formatVolume } from '@/utils/stringFormatters'
import { FileDoneOutlined } from '@ant-design/icons'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

type VolumeUnits = components['schemas']['VolumeUnits']

interface BillsOfLadingGridProps {
  bols: BOL[]
  units?: VolumeUnits | null
}

interface FlattenedBOLRow {
  id: string
  bol_number: string
  terminal: string
  sites: string
  product: string
  loaded: string
  net_volume: number
  gross_volume: number
}

export function BillsOfLadingGrid({ bols, units }: BillsOfLadingGridProps) {
  // Flatten BOLs - one row per BOL detail
  const { rowData, totals } = useMemo(() => {
    const rows: FlattenedBOLRow[] = []
    let totalNet = 0
    let totalGross = 0

    for (const bol of bols) {
      const terminal = getMappingDisplay(bol.terminal)
      const sites = bol.sites.map((s) => getMappingDisplay(s)).join(', ')
      const loaded = formatCompactDateTime(bol.bol_date)

      for (const [i, detail] of bol.details.entries()) {
        rows.push({
          id: `${bol.bol_number}-${i}`,
          bol_number: bol.bol_number,
          terminal,
          sites,
          product: getMappingDisplay(detail.product),
          loaded,
          net_volume: detail.net_volume,
          gross_volume: detail.gross_volume,
        })
        totalNet += detail.net_volume
        totalGross += detail.gross_volume
      }
    }

    return {
      rowData: rows,
      totals: { net: totalNet, gross: totalGross },
    }
  }, [bols])

  const columnDefs = useMemo<ColDef<FlattenedBOLRow>[]>(
    () => [
      {
        headerName: 'BOL #',
        field: 'bol_number',
        minWidth: 120,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Terminal',
        field: 'terminal',
        minWidth: 150,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Site',
        field: 'sites',
        minWidth: 150,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Product',
        field: 'product',
        minWidth: 120,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Loaded',
        field: 'loaded',
        minWidth: 180,
        sortable: true,
      },
      {
        headerName: 'Net',
        field: 'net_volume',
        minWidth: 100,
        sortable: true,
        valueFormatter: (params) => formatVolume(params.value, units),
      },
      {
        headerName: 'Gross',
        field: 'gross_volume',
        minWidth: 100,
        sortable: true,
        valueFormatter: (params) => formatVolume(params.value, units),
      },
    ],
    [units],
  )

  const isEmpty = rowData.length === 0

  return (
    <Vertical>
      {/* Section header */}
      <Horizontal
        alignItems='center'
        justifyContent='space-between'
        style={{ padding: '12px 0px' }}
      >
        <Horizontal
          alignItems='center'
          gap={16}
          justifyContent='space-between'
          flex={1}
        >
          <Horizontal gap={6} alignItems='center'>
            <FileDoneOutlined
              style={{ fontSize: 14, color: 'var(--gray-600)' }}
            />
            <Texto
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--gray-600)',
              }}
            >
              Bills of Lading
            </Texto>
          </Horizontal>
          {!isEmpty && (
            <Horizontal gap={8} alignItems='center'>
              <Texto style={{ fontSize: 13 }}>Total:</Texto>
              <Texto style={{ fontSize: 14, fontWeight: 600 }}>
                {formatVolume(totals.net, units)}
              </Texto>
            </Horizontal>
          )}
        </Horizontal>
      </Horizontal>

      {/* Grid */}
      <div style={{ height: 320 }}>
        <GraviGrid
          rowData={rowData}
          columnDefs={columnDefs}
          sideBar={false}
          agPropOverrides={{
            getRowId: (params) => params.data.id,
            suppressRowClickSelection: true,
            rowGroupPanelShow: 'never',
            defaultColDef: {
              sortable: true,
              resizable: true,
            },
            overlayNoRowsTemplate:
              '<span style="color: var(--muted-foreground); font-size: 13px;">No bills of lading recorded</span>',
          }}
        />
      </div>
    </Vertical>
  )
}
